/**
 * "Are you coming?" — the attendance card (D23–D27).
 *
 * Lifted out of Home unchanged so the Check in screen and Home can render the
 * same control rather than two that drift. It owns its own optimistic draft and
 * its own error line; the caller supplies nothing.
 */

import { useCallback, useState } from 'react'
import type { AttendanceStatus } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, writeOrQueue } from '../lib/api'
import { Card, Eyebrow, Notice, Segmented, SkeletonCard, Stepper } from './index'

const WEEKDAY_LONG = new Intl.DateTimeFormat(undefined, { weekday: 'long' })
const MONTH_DAY = new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric' })

/** "Sunday, September 13" for the gathering-day line (D22). */
export function gatheringLabel(dateStr: string | null): string | null {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10))
  if (!y || !m || !d) return null
  const at = new Date(y, m - 1, d, 12, 0, 0).getTime()
  return `${WEEKDAY_LONG.format(at)}, ${MONTH_DAY.format(at)}`
}

/** Three flex-weighted segments: in (filled), out (solid line), silent (dashed). */
function HeadcountBar({
  inCount, outCount, unknownCount, total,
}: { inCount: number; outCount: number; unknownCount: number; total: number }) {
  if (total <= 0) {
    return (
      <div style={{ display: 'flex', gap: 3, marginTop: 8 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'transparent', border: '1px dashed var(--line)' }} />
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 3, marginTop: 8 }} aria-hidden>
      {inCount > 0 ? <div style={{ flex: inCount, height: 6, borderRadius: 3, background: 'var(--fill)' }} /> : null}
      {outCount > 0 ? <div style={{ flex: outCount, height: 6, borderRadius: 3, background: 'var(--line)' }} /> : null}
      {unknownCount > 0 ? (
        <div style={{ flex: unknownCount, height: 6, borderRadius: 3, background: 'transparent', border: '1px dashed var(--line)' }} />
      ) : null}
    </div>
  )
}

export function CheckInCard() {
  const { state, refresh, toast } = useStore()
  const [draft, setDraft] = useState<{ status: AttendanceStatus | null; guests: number } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const week = state?.week ?? null
  const att = state?.attendance ?? null

  const status = draft ? draft.status : att?.mine ?? null
  const guests = draft ? draft.guests : att?.myGuests ?? 0

  const save = useCallback(
    async (next: AttendanceStatus | null, nextGuests: number) => {
      if (!week) return
      setDraft({ status: next, guests: nextGuests })
      setErr(null)
      setBusy(true)
      try {
        const res = await writeOrQueue(
          `/api/attendance/${week.id}`,
          'PUT',
          { status: next, guests: next === 'in' ? nextGuests : 0 },
          next === null ? 'Cleared your answer' : next === 'in' ? "You're in" : "You're out",
        )
        if (res === null) {
          toast('Saved. It will send when you’re back online')
        } else {
          await refresh(true)
          setDraft(null)
        }
      } catch (e) {
        setDraft(null)
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t save that. Try again.')
      } finally {
        setBusy(false)
      }
    },
    [week, refresh, toast],
  )

  if (!week || !att) return <SkeletonCard lines={4} />

  const dateLine = gatheringLabel(week.gatheringDate)
  const total = att.in + att.out + att.unknown

  return (
    <Card style={{ padding: 18, borderRadius: 'var(--radius-card)' }}>
      <Eyebrow large>{dateLine ? `${dateLine} · ${week.label}` : week.label}</Eyebrow>

      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 12 }}>
        {att.cutoffPassed ? 'Who’s here today' : 'Are you coming?'}
      </div>

      <div style={{ marginTop: 14 }}>
        <Segmented<AttendanceStatus>
          options={[{ value: 'in', label: "I'm in" }, { value: 'out', label: "Can't make it" }]}
          value={status}
          onChange={(v) => { if (!busy) void save(v === status ? null : v, guests) }}
        />
      </div>

      {status === 'in' ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 12 }}>
          <span style={{ fontSize: 14 }}>Bringing anyone?</span>
          <Stepper value={guests} min={0} max={5} onChange={(n) => { if (!busy) void save('in', n) }} />
        </div>
      ) : null}

      {err ? <Notice tone="alert" style={{ marginTop: 14 }}>{err}</Notice> : null}

      <div style={{ marginTop: 16, fontSize: 13, color: 'var(--dim)' }} aria-live="polite">
        {att.in} in · {att.out} out · {att.unknown} haven’t said
      </div>

      {/* No host line. Whoever's house it is knows whose house it is, and a
          field that mostly read "not set" was a chore nobody had agreed to. */}
      <HeadcountBar inCount={att.in} outCount={att.out} unknownCount={att.unknown} total={total} />
    </Card>
  )
}
